import { useParams } from 'react-router';
import { Spinner } from '@monksflow/monks-ui';
import { useQuery } from '@tanstack/react-query';
import { analysisQuery, analysisResolutionQuery } from '@/entities/analysis';
import { DeepLinkInterstitial } from '@/shared/ui';
import { isTerminalResolution, useDeepLinkResolution } from '@/shared/hooks';
import { useTranslation } from 'react-i18next';

import { AnalysisResultContent } from './AnalysisResultContent';

export const AnalysisResult = () => {
  const { t } = useTranslation('analysis');

  const { analysisId } = useParams<{ analysisId: string }>();

  // Deep-link seam: resolve the id's owning workspace first and
  // hold the analysis fetch until resolution settles — under the wrong
  // workspace header it would 404 into the error branch prematurely.
  const resolution = useDeepLinkResolution({
    resolutionQuery: analysisResolutionQuery(analysisId),
    enabled: !!analysisId,
  });

  const {
    data: analysis,
    isLoading,
    error,
  } = useQuery({
    // The seam's interstitial owns the not-found UX, not the global toast.
    ...analysisQuery(analysisId, { suppressErrorNotification: true }),
    enabled: !!analysisId && resolution.status === 'ready',
  });

  if (isLoading || resolution.status === 'resolving') {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (isTerminalResolution(resolution)) {
    return <DeepLinkInterstitial state={resolution} entity="analysis" />;
  }

  if (error || !analysis) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-destructive">{t('analysisResult.failedToLoadAnalysis')}</p>
      </div>
    );
  }

  return <AnalysisResultContent analysis={analysis} />;
};
